import {
  formatEUR,
  SAVINGS_RECURRENCE_LABELS,
  SAVINGS_RECURRENCES,
  type SavingsGoalResponse,
  type SavingsRecurrence,
} from '@budget/shared';
import { useState } from 'react';
import { ApiError } from '../api/client';
import {
  useAccounts,
  useArchiveSavingsGoal,
  useCreateSavingsGoal,
  useSavings,
  useSavingsTransfer,
  useUpdateSavingsGoal,
} from '../api/hooks';
import { AmountField } from '../components/AmountField';
import { BottomSheet } from '../components/BottomSheet';
import { GlyphPicker } from '../components/GlyphPicker';
import { goalGlyph } from '../components/glyphs';
import { GoalMeter, GoalStatus } from '../components/GoalMeter';
import { PencilIcon, TransferIcon } from '../components/TabIcons';
import { useCreationPanel } from '../components/useCreationPanel';

function localToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const DAY_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function formatDay(iso: string): string {
  // `T12:00` évite qu'un fuseau négatif fasse reculer la date d'un jour.
  return DAY_FORMAT.format(new Date(`${iso}T12:00:00`));
}

function RecurrenceField({
  value,
  onChange,
}: {
  value: SavingsRecurrence;
  onChange: (value: SavingsRecurrence) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm text-ink-secondary">Cadence de l’objectif</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as SavingsRecurrence)}
        className="rounded-xl border border-line bg-plane px-4 py-3 text-base"
      >
        {SAVINGS_RECURRENCES.map((option) => (
          <option key={option} value={option}>
            {SAVINGS_RECURRENCE_LABELS[option]}
          </option>
        ))}
      </select>
      <span className="text-xs text-ink-muted">
        {value === 'none'
          ? 'Un objectif à atteindre une fois.'
          : 'L’objectif se rejoue à chaque échéance. Le solde du pôle, lui, ne se réinitialise pas.'}
      </span>
    </label>
  );
}

function GoalCard({ goal }: { goal: SavingsGoalResponse }) {
  // La requête est partagée avec l'écran : le cache la sert, pas le réseau.
  const accounts = useAccounts();
  const transfer = useSavingsTransfer();
  const update = useUpdateSavingsGoal();
  const archive = useArchiveSavingsGoal();

  const [open, setOpen] = useState(false);
  const [amountCents, setAmountCents] = useState(0);
  const [fromAccountId, setFromAccountId] = useState(goal.fundingAccountId);

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(goal.name);
  const [targetCents, setTargetCents] = useState(goal.targetAmountCents ?? 0);
  const [targetDate, setTargetDate] = useState(goal.targetDate ?? '');
  const [monthlyCents, setMonthlyCents] = useState(goal.monthlyMinimumCents);
  const [error, setError] = useState<string | null>(null);

  const [recurrence, setRecurrence] = useState<SavingsRecurrence>(goal.recurrence);
  const [icon, setIcon] = useState(goal.icon);

  const { progress } = goal;
  const counted = goal.recurrence === 'none' ? goal.savedCents : goal.periodSavedCents;

  // Tout compte sauf celui qui porte le pôle : on ne se vire pas à soi-même.
  const sources = (accounts.data ?? []).filter((account) => account.id !== goal.accountId);

  async function sendTransfer() {
    setError(null);
    try {
      await transfer.mutateAsync({ goalId: goal.id, amountCents, fromAccountId });
      setAmountCents(0);
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Virement impossible.');
    }
  }

  async function saveChanges() {
    setError(null);
    try {
      await update.mutateAsync({
        goalId: goal.id,
        name: name.trim(),
        targetAmountCents: targetCents > 0 ? targetCents : null,
        targetDate: targetDate !== '' ? targetDate : null,
        recurrence,
        monthlyMinimumCents: monthlyCents,
        icon,
      });
      setEditing(false);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Modification impossible.');
    }
  }

  return (
    <section className="rounded-2xl border border-line bg-surface p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="flex min-w-0 items-center gap-2 text-sm font-medium">
          <span aria-hidden="true">{goalGlyph(goal)}</span>
          <span className="truncate">{goal.name}</span>
        </h2>
        <div className="flex shrink-0 items-center gap-2">
          {/* Seul l'objectif atteint se signale encore : c'est une fin, pas un
              jugement de rythme. L'avance et le retard se lisent déjà sur la
              jauge, où le repère dit tout. */}
          {progress.status === 'reached' && <GoalStatus status="reached" />}
          <button
            type="button"
            onClick={() => {
              setEditing((value) => !value);
              setOpen(false);
              setError(null);
            }}
            aria-label={`Modifier ${goal.name}`}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-line text-ink-secondary"
          >
            <PencilIcon />
          </button>
        </div>
      </div>

      <p className="mt-2 text-2xl font-semibold">
        {formatEUR(counted)}
        {goal.targetAmountCents !== null && (
          <span className="text-base font-normal text-ink-muted">
            {' '}
            sur {formatEUR(goal.targetAmountCents, { round: true })}
          </span>
        )}
      </p>

      {/* Un objectif récurrent se juge sur sa fenêtre ; le solde du pôle, lui,
          continue de vivre sa vie. On montre les deux plutôt qu'un mélange. */}
      {goal.recurrence !== 'none' && (
        <p className="text-xs text-ink-muted">
          {SAVINGS_RECURRENCE_LABELS[goal.recurrence]}
          {goal.period !== null && <> · échéance le {formatDay(goal.period.dueOn)}</>}
          {goal.savedCents !== counted && <> · solde du pôle {formatEUR(goal.savedCents)}</>}
        </p>
      )}

      {goal.targetAmountCents !== null && (
        <div className="mt-2">
          <GoalMeter progress={progress} />
        </div>
      )}

      {/* Colonne de gauche extensible : le libellé de l'effort est long, le
          montant tient en peu de place. */}
      <dl className="mt-3 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-xs">
        <dt className="text-ink-muted">Virement permanent</dt>
        <dd className="tabular text-right">{formatEUR(goal.monthlyMinimumCents)}</dd>

        {/* Le permanent part tout seul : ce qui reste à la charge de
            l'utilisateur, c'est le complément. */}
        {progress.extraMonthlyCents !== null && (
          <>
            <dt className="text-ink-muted">Effort nécessaire en plus des virements permanents</dt>
            <dd
              className={`tabular text-right ${
                progress.extraMonthlyCents > 0 ? 'text-critical' : ''
              }`}
            >
              {formatEUR(progress.extraMonthlyCents)}
              {progress.monthsRemaining !== null && progress.extraMonthlyCents > 0 && (
                <span className="text-ink-muted"> × {progress.monthsRemaining} mois</span>
              )}
            </dd>
          </>
        )}

        {goal.period !== null && goal.recurrence === 'none' && (
          <>
            <dt className="text-ink-muted">Échéance</dt>
            <dd className="tabular text-right">{formatDay(goal.period.dueOn)}</dd>
          </>
        )}
      </dl>

      {editing && (
        <div className="mt-4 flex flex-col gap-4 border-t border-line pt-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-ink-secondary">Nom du pôle</span>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="rounded-xl border border-line bg-plane px-4 py-3 text-base outline-none focus:border-series"
            />
          </label>

          <GlyphPicker label="Pictogramme" value={icon} onChange={setIcon} fallbackGlyph="🎯" />

          <AmountField
            label="Objectif"
            valueCents={targetCents}
            onChange={setTargetCents}
            hint="Laisser à zéro pour une épargne sans cible."
          />

          <RecurrenceField value={recurrence} onChange={setRecurrence} />

          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-ink-secondary">
              {recurrence === 'none' ? 'Échéance' : 'Première échéance'}
            </span>
            <input
              type="date"
              value={targetDate}
              onChange={(event) => setTargetDate(event.target.value)}
              className="rounded-xl border border-line bg-plane px-4 py-3 text-base outline-none focus:border-series"
            />
          </label>

          <AmountField
            label="Virement permanent mensuel"
            valueCents={monthlyCents}
            onChange={setMonthlyCents}
            hint="Modifier ce montant change le budget variable des mois à venir."
          />

          <p className="text-xs text-ink-muted">
            Les comptes d’un pôle ne se changent pas après coup : le sens de ses virements passés en
            dépend.
          </p>

          {error !== null && (
            <p role="alert" className="text-sm text-critical">
              {error}
            </p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex-1 rounded-xl border border-line py-3 text-sm"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={update.isPending || name.trim() === ''}
              onClick={() => void saveChanges()}
              className="flex-1 rounded-xl bg-series py-3 text-sm font-medium text-white disabled:opacity-50"
            >
              Enregistrer
            </button>
          </div>

          <button
            type="button"
            disabled={archive.isPending}
            onClick={() => archive.mutate(goal.id)}
            className="rounded-xl border border-line py-2.5 text-sm text-critical"
          >
            Archiver ce pôle
          </button>
          <p className="-mt-2 text-xs text-ink-muted">
            L’argent déjà mis de côté reste sur son compte : seul le suivi s’arrête.
          </p>
        </div>
      )}

      {!editing && (
        <button
          type="button"
          onClick={() => {
            setFromAccountId(goal.fundingAccountId);
            setError(null);
            setOpen(true);
          }}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-line py-2.5 text-sm text-ink-secondary"
        >
          <TransferIcon />
          Virement libre
        </button>
      )}

      {open && (
        <BottomSheet label={`Virement vers ${goal.name}`} onClose={() => setOpen(false)}>
          <div className="mt-4 flex flex-col gap-4">
            <AmountField
              label="Montant"
              valueCents={amountCents}
              onChange={setAmountCents}
              hint="En plus du permanent. L’argent part vraiment d’un compte vers l’autre."
            />

            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-ink-secondary">Compte émetteur</span>
              <select
                value={fromAccountId}
                onChange={(event) => setFromAccountId(event.target.value)}
                className="rounded-xl border border-line bg-plane px-4 py-3 text-base"
              >
                {sources.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
              <span className="text-xs text-ink-muted">
                Par défaut le compte qui alimente ce pôle. Un livret peut aussi renflouer un autre.
              </span>
            </label>

            {error !== null && <p className="text-xs text-critical">{error}</p>}

            <button
              type="button"
              disabled={transfer.isPending || amountCents <= 0 || fromAccountId === ''}
              onClick={() => void sendTransfer()}
              className="rounded-xl bg-series py-3 text-sm font-medium text-white disabled:opacity-50"
            >
              Virer
            </button>
          </div>
        </BottomSheet>
      )}
    </section>
  );
}

export function SavingsScreen() {
  const savings = useSavings();
  const accounts = useAccounts();
  const create = useCreateSavingsGoal();

  const [open, setOpen] = useCreationPanel();
  const [name, setName] = useState('');
  const [targetCents, setTargetCents] = useState(0);
  const [targetDate, setTargetDate] = useState('');
  const [monthlyCents, setMonthlyCents] = useState(0);
  const [accountId, setAccountId] = useState('');
  const [recurrence, setRecurrence] = useState<SavingsRecurrence>('none');
  const [icon, setIcon] = useState('');
  const [error, setError] = useState<string | null>(null);

  const savingsAccounts = (accounts.data ?? []).filter((item) => item.type === 'savings');
  const checking = (accounts.data ?? []).find((item) => item.type === 'checking');
  const goals = savings.data?.goals ?? [];

  async function submit() {
    // Chaque refus dit sa raison : un bouton qui ne réagit pas ressemble à une
    // panne, surtout sur une base neuve où il manque encore un compte.
    const target = accountId !== '' ? accountId : savingsAccounts[0]?.id;
    if (checking === undefined) {
      setError('Créez d’abord un compte courant : c’est lui qui alimente le pôle.');
      return;
    }
    if (target === undefined) {
      setError('Créez d’abord un compte d’épargne : c’est là que l’argent dort.');
      return;
    }
    if (name.trim() === '') {
      setError('Donnez un nom au pôle.');
      return;
    }

    setError(null);
    try {
      await create.mutateAsync({
        name: name.trim(),
        accountId: target,
        fundingAccountId: checking.id,
        targetAmountCents: targetCents > 0 ? targetCents : null,
        targetDate: targetDate !== '' ? targetDate : null,
        recurrence,
        monthlyMinimumCents: monthlyCents,
        startedOn: localToday(),
        icon,
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Création impossible.');
      return;
    }

    setName('');
    setTargetCents(0);
    setTargetDate('');
    setMonthlyCents(0);
    setIcon('');
    setOpen(false);
  }

  return (
    <div className="px-5 pt-4">
      <h1 className="text-xl font-semibold">Épargne</h1>

      {savingsAccounts.length === 0 && (
        <p className="mt-4 rounded-xl border border-line bg-surface p-4 text-sm text-ink-secondary">
          Créez d’abord un compte d’épargne : un pôle a besoin d’un endroit où l’argent dort
          vraiment.
        </p>
      )}

      <div className="mt-4 flex flex-col gap-3">
        {goals.map((goal) => (
          <GoalCard key={goal.id} goal={goal} />
        ))}
      </div>

      {goals.length === 0 && savingsAccounts.length > 0 && (
        <p className="mt-4 text-sm text-ink-secondary">
          Aucun pôle pour l’instant. Un pôle, c’est un objectif nommé — vacances, travaux — avec une
          échéance.
        </p>
      )}

      {savings.data !== undefined && savings.data.unassignedCents !== 0 && (
        <p className="mt-4 rounded-xl border border-line bg-surface p-3 text-xs text-ink-secondary">
          {formatEUR(savings.data.unassignedCents)} sur vos comptes d’épargne ne sont rattachés à
          aucun pôle.
        </p>
      )}

      {open && (
        <BottomSheet
          label="Nouveau pôle d’épargne"
          onClose={() => {
            setOpen(false);
            setError(null);
          }}
        >
          <div className="mt-4 flex flex-col gap-4">
            {/* Le bouton d'action ouvre toujours ce formulaire : c'est ici qu'il
              faut dire ce qui manque, plutôt que sur un bouton grisé. */}
            {savingsAccounts.length === 0 && (
              <p className="rounded-xl border border-line bg-plane p-3 text-sm text-ink-secondary">
                Créez d’abord un compte d’épargne : un pôle a besoin d’un endroit où l’argent dort
                vraiment.
              </p>
            )}

            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-ink-secondary">Nom du pôle</span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Vacances"
                className="rounded-xl border border-line bg-plane px-4 py-3 text-base outline-none focus:border-series"
              />
            </label>

            {savingsAccounts.length > 1 && (
              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-ink-secondary">Où dort l’argent</span>
                <select
                  value={accountId !== '' ? accountId : savingsAccounts[0]!.id}
                  onChange={(event) => setAccountId(event.target.value)}
                  className="rounded-xl border border-line bg-plane px-4 py-3 text-base"
                >
                  {savingsAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <GlyphPicker label="Pictogramme" value={icon} onChange={setIcon} fallbackGlyph="🎯" />

            <AmountField label="Objectif" valueCents={targetCents} onChange={setTargetCents} />

            <RecurrenceField value={recurrence} onChange={setRecurrence} />

            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-ink-secondary">
                {recurrence === 'none' ? 'Échéance' : 'Première échéance'}
              </span>
              <input
                type="date"
                value={targetDate}
                onChange={(event) => setTargetDate(event.target.value)}
                className="rounded-xl border border-line bg-plane px-4 py-3 text-base outline-none focus:border-series"
              />
              <span className="text-xs text-ink-muted">
                C’est elle qui permet de recalculer l’effort quand on prend du retard.
              </span>
            </label>

            <AmountField
              label="Virement permanent mensuel"
              valueCents={monthlyCents}
              onChange={setMonthlyCents}
              hint="C’est ce montant que le budget retient chaque mois."
            />

            {error !== null && (
              <p role="alert" className="text-sm text-critical">
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 rounded-xl border border-line py-3 text-sm"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={create.isPending}
                onClick={() => void submit()}
                className="flex-1 rounded-xl bg-series py-3 text-sm font-medium text-white disabled:opacity-50"
              >
                Créer le pôle
              </button>
            </div>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}
